import { SyntheticEvent, useState } from 'react';

import {
  Box,
  Button,
  Grid,
  Tab,
  Tabs,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';

import DateFnsUtils from '@date-io/date-fns';
import {
  endOfMonth,
  endOfQuarter,
  endOfYear,
  format,
  getQuarter,
  isSameMonth,
  isSameQuarter,
  isSameYear,
  startOfDay,
  startOfMonth,
  startOfQuarter,
  startOfYear,
} from 'date-fns';

import { FilterDateRange } from './FilterDateRange';

interface DateRangePickerProps {
  value: FilterDateRange;
  onChange: (value: FilterDateRange) => void;
  onClose?: () => void;
}

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    maxWidth: 500,
    minWidth: 500,
    backgroundColor: theme.palette.background.paper,
  },
  monthGrid: {
    padding: theme.spacing(2),
    maxHeight: 400,
    overflowY: 'auto',
  },
  monthButton: {
    width: '100%',
    textTransform: 'none',
    padding: theme.spacing(1),
  },
  quarterButton: {
    width: '100%',
    textTransform: 'none',
    padding: theme.spacing(2),
  },
  halfYearButton: {
    width: '100%',
    textTransform: 'none',
    padding: theme.spacing(3),
  },
  yearButton: {
    width: '100%',
    textTransform: 'none',
    padding: theme.spacing(3),
  },
  yearDivider: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
}));

type GranularityType = 'day' | 'month' | 'quarter' | 'half-year' | 'year';

export const getHalfYearRange = (
  date: Date,
): { start: string; end: string } => {
  const isFirstHalf = date.getMonth() < 6;
  const start = new Date(
    date.getFullYear(),
    isFirstHalf ? 0 : 6,
    1,
  ).toISOString();
  const end = endOfMonth(
    new Date(date.getFullYear(), isFirstHalf ? 5 : 11, 1),
  ).toISOString();
  return { start, end };
};

export const DateRangePicker = ({
  value,
  onChange,
  onClose,
}: DateRangePickerProps) => {
  const classes = useStyles();
  const [granularity, setGranularity] = useState<GranularityType>('day');
  const viewDate = new Date();

  const handleGranularityChange = (
    _event: SyntheticEvent<EventTarget>,
    newValue: GranularityType,
  ) => {
    setGranularity(newValue);
  };

  const handleDaySelect = (date: Date | null) => {
    if (date) {
      const start = startOfDay(date).toISOString();
      onChange({ start });
    }
  };

  const handleMonthSelect = (date: Date) => {
    const start = startOfMonth(date).toISOString();
    const end = endOfMonth(date).toISOString();
    onChange({ start, end });
  };

  const handleQuarterSelect = (date: Date) => {
    const start = startOfQuarter(date).toISOString();
    const end = endOfQuarter(date).toISOString();
    onChange({ start, end });
  };

  const handleHalfYearSelect = (date: Date) => {
    onChange(getHalfYearRange(date));
  };

  const handleYearSelect = (year: number) => {
    const start = startOfYear(new Date(year, 0, 1)).toISOString();
    const end = endOfYear(new Date(year, 0, 1)).toISOString();
    onChange({ start, end });
  };

  const renderDayPicker = () => {
    return (
      <Box display="flex" justifyContent="center" alignItems="center">
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
          <DatePicker
            autoOk
            orientation="landscape"
            variant="static"
            openTo="date"
            value={value.start ? new Date(value.start) : null}
            onChange={handleDaySelect}
            disableToolbar
          />
        </MuiPickersUtilsProvider>
      </Box>
    );
  };

  const renderMonthGrid = () => {
    const currentYear = viewDate.getFullYear();
    const years = [currentYear - 1, currentYear];

    return years.map(year => (
      <Box key={year}>
        <Typography variant="h6" className={classes.yearDivider}>
          {year}
        </Typography>
        <Grid container spacing={1}>
          {Array.from({ length: 12 }, (_, i) => {
            const date = new Date(year, i, 1);
            const isSelected =
              value.start && isSameMonth(new Date(value.start), date);

            return (
              <Grid item xs={4} key={i}>
                <Button
                  variant={isSelected ? 'contained' : 'outlined'}
                  color={isSelected ? 'primary' : 'default'}
                  className={classes.monthButton}
                  onClick={() => handleMonthSelect(date)}
                >
                  {format(date, 'MMM')}
                </Button>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    ));
  };

  const renderQuarterGrid = () => {
    const currentYear = viewDate.getFullYear();
    const years = [currentYear - 1, currentYear];

    return years.map(year => (
      <Box key={year}>
        <Typography variant="h6" className={classes.yearDivider}>
          {year}
        </Typography>
        <Grid container spacing={2}>
          {Array.from({ length: 4 }, (_, i) => {
            const date = new Date(year, i * 3, 1);
            const isSelected =
              value.start && isSameQuarter(new Date(value.start), date);

            return (
              <Grid item xs={6} key={i}>
                <Button
                  variant={isSelected ? 'contained' : 'outlined'}
                  color={isSelected ? 'primary' : 'default'}
                  className={classes.quarterButton}
                  onClick={() => handleQuarterSelect(date)}
                >
                  {`Q${getQuarter(date)}`}
                </Button>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    ));
  };

  const renderHalfYearGrid = () => {
    const currentYear = viewDate.getFullYear();
    const years = [currentYear - 1, currentYear];

    return years.map(year => (
      <Box key={year}>
        <Typography variant="h6" className={classes.yearDivider}>
          {year}
        </Typography>
        <Grid container spacing={2}>
          {[0, 6].map((month, i) => {
            const date = new Date(year, month, 1);
            const isSelected =
              value.start &&
              new Date(value.start).getFullYear() === year &&
              Math.floor(new Date(value.start).getMonth() / 6) === i;

            return (
              <Grid item xs={6} key={i}>
                <Button
                  variant={isSelected ? 'contained' : 'outlined'}
                  color={isSelected ? 'primary' : 'default'}
                  className={classes.halfYearButton}
                  onClick={() => handleHalfYearSelect(date)}
                >
                  {`H${i + 1}`}
                </Button>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    ));
  };

  const renderYearGrid = () => {
    const currentYear = viewDate.getFullYear();
    const years = Array.from({ length: 4 }, (_, i) => currentYear - (3 - i));

    return (
      <Box>
        <Grid container spacing={2}>
          {years.map(year => {
            const date = new Date(year, 0, 1);
            const isSelected =
              value.start && isSameYear(new Date(value.start), date);

            return (
              <Grid item xs={6} key={year}>
                <Button
                  variant={isSelected ? 'contained' : 'outlined'}
                  color={isSelected ? 'primary' : 'default'}
                  className={classes.yearButton}
                  onClick={() => handleYearSelect(year)}
                >
                  {year}
                </Button>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    );
  };

  return (
    <div className={classes.root}>
      <Box>
        <Typography variant="subtitle1" style={{ padding: '16px 16px 0' }}>
          Created date <span>{granularity === 'day' ? 'after' : 'during'}</span>
        </Typography>
        <Tabs
          value={granularity}
          onChange={handleGranularityChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab label="Day" value="day" />
          <Tab label="Month" value="month" />
          <Tab label="Quarter" value="quarter" />
          <Tab label="Half-year" value="half-year" />
          <Tab label="Year" value="year" />
        </Tabs>
      </Box>

      <Box className={classes.monthGrid}>
        {granularity === 'day' && renderDayPicker()}
        {granularity === 'month' && renderMonthGrid()}
        {granularity === 'quarter' && renderQuarterGrid()}
        {granularity === 'half-year' && renderHalfYearGrid()}
        {granularity === 'year' && renderYearGrid()}
      </Box>

      <Box className={classes.footer}>
        <Button onClick={onClose}>Cancel</Button>
        <Button color="primary" onClick={onClose}>
          Apply
        </Button>
      </Box>
    </div>
  );
};
